/**
 * jQuery bridge (WordPress platforms only)
 *
 * The library core communicates exclusively over pmw.bus. On WordPress
 * shops, years of documented customer snippets speak jQuery instead. This
 * bridge keeps every one of them working, indefinitely, in both
 * directions:
 *
 * OUTBOUND - every pmw.bus emit is mirrored as a jQuery event on document,
 * so snippets like
 *   jQuery(document).on("pmw:event:purchase", (event, payload) => {...})
 * keep receiving events. (Documented in the docs "JavaScript events"
 * and "Tips & Tricks" pages; the docs' new examples use pmw.bus, this
 * mirror carries the legacy ones.)
 *
 * INBOUND - snippets that dispatch entry events via jQuery, e.g.
 *   jQuery(document).trigger("pmw:add-to-cart", product)
 * are forwarded into the bus. The documented entry events are forwarded
 * from the moment this file loads; every other event name gets its
 * forwarder as soon as the library subscribes to it, so inbound coverage
 * always matches the library's real listener surface.
 *
 * Mirrored triggers carry a `pmwMirrored` tag so the inbound forwarder
 * recognizes the bridge's own echo and does not feed it back into the bus
 * (jQuery triggers synchronously, so the two run inside each other).
 *
 * Events the library also dispatches natively on document are left to that
 * dispatch: jQuery handlers bound to document receive native DOM events
 * too, so mirroring those as well fired every legacy handler twice.
 *
 * The legacy wpm* event names are NOT handled here; they live in
 * pmw/deprecated.mjs, which translates them to pmw:* jQuery events that
 * this bridge then forwards inbound.
 *
 * This file must load right after pmw/bus.js and before any listener
 * registrations.
 *
 * @since 1.65.0
 */

(function (pmw) {

	// Non-WordPress platforms (or a WP page without jQuery) have no legacy
	// snippets to bridge.
	if (typeof jQuery === "undefined") return;

	const $ = jQuery;

	/**
	 * Property set on every jQuery event the outbound mirror triggers, so the
	 * inbound forwarder can tell the bridge's own echo apart from a genuine
	 * customer trigger.
	 *
	 * This used to be a module-level `mirroring` boolean, which was not
	 * re-entrant and turned our own documented snippets into infinite loops:
	 * a jQuery handler that synchronously emits a bus event (as the parent
	 * view_item snippet does) starts a nested mirror, whose `finally` clears
	 * the flag while the outer trigger is still dispatching. The forwarders
	 * further down that same outer trigger then saw `mirroring === false`,
	 * fed the event back into the bus, and the page died with a call stack
	 * overflow. A tag travels with the event instead of with the module, so
	 * nesting cannot confuse it.
	 *
	 * @since 1.66.1
	 * @type {string}
	 */
	const mirrorTag = "pmwMirrored";

	/** Event names that already have an inbound jQuery -> bus forwarder. */
	const forwarded = new Set();

	const forwardInbound = (eventName) => {
		if (forwarded.has(eventName)) return;
		forwarded.add(eventName);

		$(document).on(eventName, (event, payload) => {

			// The bridge's own outbound mirror of a bus emit. Feeding it back
			// in would re-emit what the bus just emitted.
			if (event[mirrorTag]) return;

			// Native DOM events (event.originalEvent set) are the library's
			// own dual dispatches (bus.emit + dispatchEvent for third-party
			// addEventListener snippets); forwarding them would double-fire.
			// Only synthetic jQuery triggers (customer snippets,
			// deprecated.mjs) are forwarded into the bus.
			if (event.originalEvent) return;

			pmw.bus.emit(eventName, payload);
		});
	};

	// OUTBOUND: mirror every bus emit onto jQuery for legacy snippets.
	pmw.bus.observeEmits((eventName, payload) => {

		// Events the library also dispatches as native DOM events reach
		// jQuery handlers on document through that dispatch already. Mirroring
		// them on top ran every legacy handler twice.
		//
		// @since 1.66.1
		if (typeof pmw.bus.isDomDispatched === "function" && pmw.bus.isDomDispatched(eventName)) return;

		$(document).trigger($.Event(eventName, {[mirrorTag]: true}), payload);
	});

	// INBOUND: the entry events are accepted from the moment this bridge
	// loads, not only once the library subscribes to them. The library's own
	// subscriptions happen in the platform's event listener file, which loads
	// several awaits later, and a jQuery trigger before that point would
	// otherwise not even reach the bus - the bus buffers what it receives too
	// early, but it cannot buffer what never arrives.
	//
	// @since 1.65.2
	if (typeof pmw.bus.entryEventNames === "function") {
		for (const eventName of pmw.bus.entryEventNames()) {
			forwardInbound(eventName);
		}
	}

	// Whatever else the library subscribes to is accepted from legacy jQuery
	// triggers as well, so inbound coverage keeps matching the real listener
	// surface.
	const originalOn = pmw.bus.on;
	pmw.bus.on = function (eventName, handler) {
		forwardInbound(eventName);
		return originalOn.call(pmw.bus, eventName, handler);
	};

}(window.pmw = window.pmw || {}));
